/**
 * Maps domain status values to semantic colour classes from tokens.css.
 */

import type {
  BidStatus,
  ShipmentStatus,
  DocumentStatus,
  KycStatus,
  InterestStatus,
} from '../../api/types';
import type { StatusIconName } from '../ui/StatusIcon';

export function shipmentStatusColor(status: ShipmentStatus): {
  bg: string;
  text: string;
} {
  switch (status) {
    case 'POSTED':
      return { bg: 'var(--status-amber-bg)', text: 'var(--status-amber-text)' };
    case 'AWARDED':
    case 'ASSIGNED':
    case 'EN_ROUTE_PICKUP':
    case 'AT_PICKUP':
    case 'LOADED':
    case 'IN_TRANSIT':
    case 'AT_DROP':
      return { bg: 'var(--status-green-bg)', text: 'var(--status-green-text)' };
    case 'DELIVERED':
    case 'COMPLETED':
      return { bg: 'var(--status-green-bg-2)', text: 'var(--status-green-text-2)' };
    case 'EXCEPTION':
      return { bg: 'var(--status-red-bg)', text: 'var(--status-red-text)' };
    case 'DRAFT':
    case 'CANCELLED':
    case 'EXPIRED':
      return { bg: 'var(--status-gray-bg)', text: 'var(--status-gray-text)' };
    default:
      return { bg: 'var(--status-gray-bg)', text: 'var(--status-gray-text)' };
  }
}

/** A truck with an open stop (dnm-work#165): amber = needs attention, shown next to the real stage. */
export function stoppedColor(): { bg: string; text: string } {
  return { bg: 'var(--status-amber-bg)', text: 'var(--status-amber-text)' };
}

export function documentStatusColor(status: DocumentStatus): {
  bg: string;
  text: string;
} {
  switch (status) {
    case 'VERIFIED':
      return { bg: 'var(--status-green-bg)', text: 'var(--status-green-text)' };
    case 'UPLOADED':
    case 'PENDING':
      return { bg: 'var(--status-amber-bg)', text: 'var(--status-amber-text)' };
    case 'REJECTED':
      return { bg: 'var(--status-red-bg)', text: 'var(--status-red-text)' };
  }
}

export function kycStatusColor(status: KycStatus): { bg: string; text: string } {
  switch (status) {
    case 'VERIFIED':
      return { bg: 'var(--status-green-bg)', text: 'var(--status-green-text)' };
    case 'SUBMITTED':
      return { bg: 'var(--status-amber-bg)', text: 'var(--status-amber-text)' };
    case 'PENDING':
      return { bg: 'var(--status-gray-bg)', text: 'var(--status-gray-text)' };
    case 'REJECTED':
      return { bg: 'var(--status-red-bg)', text: 'var(--status-red-text)' };
  }
}

export function interestStatusColor(status: InterestStatus): { bg: string; text: string } {
  switch (status) {
    case 'NEW':
      return { bg: 'var(--status-amber-bg)', text: 'var(--status-amber-text)' };
    case 'CONTACTING':
      return { bg: 'var(--status-amber-bg)', text: 'var(--status-amber-text)' };
    case 'FOLLOW_UP':
      return { bg: 'var(--action-primary-soft)', text: 'var(--accent)' };
    case 'CONVERTED_TO_BID':
      return { bg: 'var(--status-green-bg)', text: 'var(--status-green-text)' };
    case 'WITHDRAWN':
    case 'CLOSED':
    default:
      return { bg: 'var(--status-gray-bg)', text: 'var(--status-gray-text)' };
  }
}

const BID_STATUS_DISPLAY: Record<
  BidStatus,
  { label: string; icon: StatusIconName; tone: 'amber' | 'green' | 'red' | 'gray' }
> = {
  PENDING: { label: 'Pending', icon: 'schedule', tone: 'amber' },
  ACCEPTED: { label: 'Accepted', icon: 'check', tone: 'green' },
  REJECTED: { label: 'Rejected', icon: 'close', tone: 'red' },
  EXPIRED: { label: 'Expired', icon: 'hourglass', tone: 'gray' },
  WITHDRAWN: { label: 'Withdrawn', icon: 'undo', tone: 'gray' },
  // Amber, not red: a reversed award is not the bidder losing, ops must tell it apart from Rejected.
  AWARD_REVERSED: { label: 'Award reversed', icon: 'history', tone: 'amber' },
};

/** One mapping for every bid table. Unknown statuses read as a gray pill with the word title-cased. */
export function bidStatusDisplay(status: string): {
  bg: string;
  text: string;
  label: string;
  icon: StatusIconName | undefined;
} {
  const known = BID_STATUS_DISPLAY[status as BidStatus];
  const tone = known?.tone ?? 'gray';
  const words = status.replace(/_/g, ' ').trim().toLowerCase();
  return {
    bg: `var(--status-${tone}-bg)`,
    text: `var(--status-${tone}-text)`,
    label: known?.label ?? (words ? words[0].toUpperCase() + words.slice(1) : 'Unknown'),
    icon: known?.icon,
  };
}
